<template>
  <div class="leads-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <v-container class="leads-container">
      <!-- Top Section: Score + Quick Add -->
      <div class="top-section">
        <!-- Your Score Card -->
        <div class="score-card">
          <div class="score-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Your Score</span>
          </div>

          <div class="score-stats">
            <div class="score-main">
              <div class="score-value">{{ stats.points }}</div>
              <div class="score-label">points</div>
            </div>
            <div class="score-today">
              <span class="score-today-value">+{{ displayTodayPoints }}</span>
              <span class="score-today-label">today</span>
            </div>
          </div>

          <div class="score-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressWidth + '%' }"></div>
            </div>
            <div class="progress-text">
              {{ nextBadgeTier ? `Next badge in ${badgeRemaining} pts` : 'All badges earned' }}
            </div>
          </div>

          <div class="score-badges">
            <div v-if="displayStreakDays > 0" class="streak-badge">
              <span class="streak-emoji">🔥</span>
              <span>{{ displayStreakDays }} day streak</span>
            </div>
            <div v-if="earnedBadges.length === 0 && displayStreakDays === 0" class="badge-pill">
              Log your first activity to start earning badges
            </div>
            <div v-for="badge in earnedBadges" :key="badge" class="badge-pill">
              {{ badge }}
            </div>
          </div>
        </div>

        <!-- Quick Add Lead Card -->
        <div class="add-lead-card">
          <div class="add-lead-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21M4 7H20M6 21H18C19.1046 21 20 20.1046 20 19V9C20 7.89543 19.1046 7 18 7H6C4.89543 7 4 7.89543 4 9V19C4 20.1046 4.89543 21 6 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 11V17M9 14H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Quick Add Lead</span>
          </div>

          <form @submit.prevent="createLead" class="add-lead-form">
            <div class="form-row">
              <div class="form-field">
                <label>Club Name *</label>
                <input v-model="leadForm.companyName" type="text" placeholder="Fairview Country Club" required />
              </div>
              <div class="form-field">
                <label>Contact Name</label>
                <input v-model="leadForm.contactName" type="text" placeholder="Jane Smith" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <label>Email</label>
                <input v-model="leadForm.email" type="email" placeholder="jane@fairviewcc.com" />
              </div>
              <div class="form-field">
                <label>Phone</label>
                <input v-model="leadForm.phone" type="tel" placeholder="+1 (555) 123-4567" />
              </div>
            </div>

            <div class="form-field">
              <label>Notes</label>
              <textarea v-model="leadForm.notes" rows="2" placeholder="Any additional information..."></textarea>
            </div>

            <button type="submit" class="btn-save-lead">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 2.5H5C3.61929 2.5 2.5 3.61929 2.5 5V15C2.5 16.3807 3.61929 17.5 5 17.5H15C16.3807 17.5 17.5 16.3807 17.5 15V5C17.5 3.61929 16.3807 2.5 15 2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.5 2.5V7.5H7.5V2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Save Lead</span>
            </button>
          </form>
        </div>
      </div>

      <!-- Pipeline Section -->
      <div class="pipeline-section">
        <div class="pipeline-card">
          <div class="pipeline-header">
            <div class="pipeline-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Your Pipeline</span>
              <div class="lead-count">{{ filteredLeads.length }} leads</div>
            </div>

            <div class="pipeline-controls">
              <!-- View Toggle -->
              <div class="view-toggle">
                <button
                  :class="['view-btn', { active: viewMode === 'all' }]"
                  @click="viewMode = 'all'"
                >
                  All Team
                </button>
                <button
                  :class="['view-btn', { active: viewMode === 'mine' }]"
                  @click="viewMode = 'mine'"
                >
                  My Leads
                </button>
              </div>

              <!-- Stage Filter -->
              <div class="filter-group">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M2.5 5H17.5M2.5 10H17.5M2.5 15H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <select v-model="stageFilter" class="stage-select">
                  <option value="">All open</option>
                  <option v-for="stage in stages" :key="stage" :value="stage">{{ stage }}</option>
                </select>
              </div>

              <!-- Search -->
              <div class="search-box">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <input v-model="search" type="text" placeholder="Search leads..." />
              </div>
            </div>
          </div>

          <!-- Leads Table -->
          <div v-if="!loading && filteredLeads.length > 0" class="leads-table">
            <div class="table-header">
              <div class="th th-lead">Lead</div>
              <div class="th th-stage">Stage</div>
              <div class="th th-contact">Last Contact</div>
              <div class="th th-next">Next Action</div>
              <div class="th th-actions">Actions</div>
            </div>

            <div class="table-body">
              <div
                v-for="lead in paginatedLeads"
                :key="lead.id"
                :class="['lead-row', { 'due-soon': isDueSoon(lead) }]"
              >
                <div class="td td-lead">
                  <div class="lead-avatar" :style="{ background: getAvatarColor(lead.companyName) }">
                    {{ initials(lead.companyName) }}
                  </div>
                  <div class="lead-info">
                    <div class="lead-company">{{ lead.companyName }}</div>
                    <div class="lead-contact">{{ lead.contactName || '—' }} • {{ lead.email || 'No email' }}</div>
                  </div>
                </div>

                <div class="td td-stage">
                  <span :class="['stage-badge', 'stage-' + lead.status]">
                    {{ lead.status }}
                  </span>
                </div>

                <div class="td td-contact">
                  <span class="contact-date">{{ formatDate(lead.lastContactedAt) }}</span>
                </div>

                <div class="td td-next">
                  <div v-if="lead.nextAction && lead.nextAction.type" class="next-action">
                    <span :class="['action-type', 'action-' + lead.nextAction.type]">
                      {{ lead.nextAction.type }}
                    </span>
                    <span class="action-due">{{ formatDue(lead.nextAction.dueAt) }}</span>
                  </div>
                  <button v-else class="btn-set-action" @click="openLog(lead, 'call')">
                    Set action
                  </button>
                </div>

                <div class="td td-actions">
                  <div class="action-buttons">
                    <button class="action-icon" title="Log call" @click="openLog(lead, 'call')">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M5.5 2.5C4 2.5 2.5 4 2.5 5.5C2.5 11 7 15.5 12.5 15.5C14 15.5 15.5 14 15.5 12.5L12 10L10 11.5C9 11 7 9 6.5 8L8 6L5.5 2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>

                    <button class="action-icon" title="Log email" @click="openLog(lead, 'email')">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M3 3H15C15.825 3 16.5 3.675 16.5 4.5V13.5C16.5 14.325 15.825 15 15 15H3C2.175 15 1.5 14.325 1.5 13.5V4.5C1.5 3.675 2.175 3 3 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M16.5 4.5L9 9.75L1.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>

                    <button class="action-icon" title="Log meeting" @click="openLog(lead, 'meeting')">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M6 8.25C7.65685 8.25 9 6.90685 9 5.25C9 3.59315 7.65685 2.25 6 2.25C4.34315 2.25 3 3.59315 3 5.25C3 6.90685 4.34315 8.25 6 8.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M1.5 15.75V14.25C1.5 13.4544 1.81607 12.6913 2.37868 12.1287C2.94129 11.5661 3.70435 11.25 4.5 11.25H7.5C8.29565 11.25 9.05871 11.5661 9.62132 12.1287C10.1839 12.6913 10.5 13.4544 10.5 14.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>

                    <button class="action-icon action-icon-view" title="View details" @click="openDetails(lead)">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M1.5 9C1.5 9 3.75 3.75 9 3.75C14.25 3.75 16.5 9 16.5 9C16.5 9 14.25 14.25 9 14.25C3.75 14.25 1.5 9 1.5 9Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>

                    <!-- Stage Dropdown -->
                    <div class="stage-dropdown">
                      <button class="action-icon" title="Move stage">
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                      <div class="stage-menu">
                        <div
                          v-for="stage in stages"
                          :key="stage"
                          class="stage-menu-item"
                          @click="moveStage(lead, stage)"
                        >
                          {{ stage }}
                        </div>
                      </div>
                    </div>

                    <button class="btn-won" @click="closeDeal(lead, true)">Won</button>
                    <button class="btn-lost" @click="closeDeal(lead, false)">Lost</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="table-pagination">
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                Previous
              </button>
              <div class="page-info">
                Page {{ currentPage }} of {{ totalPages }}
              </div>
              <button
                class="page-btn"
                :disabled="currentPage === totalPages"
                @click="currentPage++"
              >
                Next
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="loading" class="table-loading">
            <div class="loader"></div>
            <p>Loading your leads...</p>
          </div>

          <!-- Empty State -->
          <div v-else class="table-empty">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <path d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M32 20V32L40 36" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3>No leads found</h3>
            <p>{{ viewMode === 'mine' ? "You haven't created any leads yet." : 'Change your filters or add a new lead above.' }}</p>
          </div>
        </div>
      </div>
    </v-container>

    <!-- Log Interaction Modal -->
    <div v-if="logDialog.open" class="modal-overlay" @click="closeModal">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h3>Log {{ logDialog.type }} – {{ logDialog.lead?.companyName }}</h3>
          <button class="modal-close" @click="closeModal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-field">
            <label>Notes</label>
            <textarea v-model="logDialog.note" rows="3" placeholder="What happened during this interaction?"></textarea>
          </div>

          <div class="form-field">
            <label>Outcome</label>
            <select v-model="logDialog.outcome">
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
            </select>
          </div>

          <div class="modal-divider"></div>

          <div class="modal-section-title">Next Action (optional)</div>
          <div class="form-row">
            <div class="form-field">
              <label>Type</label>
              <select v-model="nextAction.type">
                <option value="">None</option>
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="proposal">Proposal</option>
                <option value="demo">Demo</option>
              </select>
            </div>
            <div class="form-field">
              <label>Due Date</label>
              <input v-model="nextAction.dueAt" type="datetime-local" />
            </div>
          </div>

          <div class="form-field">
            <label>Reminder Note</label>
            <input v-model="nextAction.note" type="text" placeholder="Optional reminder..." />
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-modal-cancel" @click="closeModal">Cancel</button>
          <button class="btn-modal-save" @click="saveInteraction">Save Interaction</button>
        </div>
      </div>
    </div>

    <!-- Lead Details Modal -->
    <div v-if="detailsDialog.open" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-card modal-card-large" @click.stop>
        <div class="modal-header">
          <h3>Lead Details</h3>
          <button class="modal-close" @click="closeDetailsModal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body modal-body-details" v-if="detailsDialog.lead">
          <div class="details-grid">
            <div class="details-main">
              <div class="details-avatar" :style="{ background: getAvatarColor(detailsDialog.lead.companyName) }">
                {{ initials(detailsDialog.lead.companyName) }}
              </div>
              <h2 class="details-company">{{ detailsDialog.lead.companyName }}</h2>
              <span :class="['details-stage', 'stage-' + detailsDialog.lead.status]">
                {{ detailsDialog.lead.status }}
              </span>

              <div class="details-info">
                <div class="info-row">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 10C11.6569 10 13 8.65685 13 7C13 5.34315 11.6569 4 10 4C8.34315 4 7 5.34315 7 7C7 8.65685 8.34315 10 10 10Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M10 12C6.68629 12 4 14.6863 4 18H16C16 14.6863 13.3137 12 10 12Z" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <span>{{ detailsDialog.lead.contactName || 'No contact name' }}</span>
                </div>

                <div class="info-row" v-if="detailsDialog.lead.email">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M3 3H17C17.825 3 18.5 3.675 18.5 4.5V15.5C18.5 16.325 17.825 17 17 17H3C2.175 17 1.5 16.325 1.5 15.5V4.5C1.5 3.675 2.175 3 3 3Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M18.5 4.5L10 10.75L1.5 4.5" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <a :href="'mailto:' + detailsDialog.lead.email">{{ detailsDialog.lead.email }}</a>
                </div>

                <div class="info-row" v-if="detailsDialog.lead.phone">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M18.3333 14.1V16.6C18.3341 16.8321 18.2868 17.062 18.1944 17.2752C18.102 17.4885 17.9666 17.6807 17.7966 17.8398C17.6266 17.9989 17.4257 18.1212 17.2065 18.1989C16.9872 18.2767 16.7544 18.3083 16.5225 18.2917C13.9287 18.0154 11.4383 17.1069 9.24725 15.6347C7.23997 14.3025 5.55297 12.5155 4.30558 10.4083C2.83084 8.09906 1.95338 5.47838 1.74225 2.75833C1.72564 2.51854 1.7569 2.27769 1.83347 2.04992C1.91004 1.82216 2.03027 1.61227 2.18676 1.43365C2.34324 1.25503 2.53259 1.1114 2.74395 1.01041C2.95531 0.90942 3.18412 0.853359 3.41642 0.850003H5.74975C6.1685 0.845847 6.57301 1.00631 6.88253 1.29848C7.19205 1.59064 7.38279 1.99072 7.42475 2.41667C7.50204 3.26806 7.66608 4.10852 7.91392 4.92417C8.03197 5.32695 8.03968 5.75329 7.93657 6.16021C7.83346 6.56712 7.62371 6.93919 7.33308 7.24167L6.30142 8.33333C7.58063 10.5866 9.42134 12.5266 11.5581 13.875L12.5897 12.8083C12.8754 12.4972 13.2285 12.2745 13.6162 12.1631C14.0039 12.0518 14.4115 12.0558 14.7972 12.175C15.5679 12.4407 16.3621 12.6158 17.1689 12.6972C17.5746 12.7413 17.9567 12.9449 18.2367 13.2703C18.5168 13.5957 18.6742 14.0198 18.6772 14.4583L18.3333 14.1Z" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <a :href="'tel:' + detailsDialog.lead.phone">{{ detailsDialog.lead.phone }}</a>
                </div>
              </div>

              <div class="details-meta">
                <div class="meta-item">
                  <span class="meta-label">Created</span>
                  <span class="meta-value">{{ formatDate(detailsDialog.lead.createdAt) }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Last Contact</span>
                  <span class="meta-value">{{ formatDate(detailsDialog.lead.lastContactedAt) || 'Never' }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Owner</span>
                  <span class="meta-value">{{ detailsDialog.lead.ownerUid === currentUid ? 'You' : 'Team member' }}</span>
                </div>
              </div>

              <div v-if="detailsDialog.lead.notes" class="details-notes">
                <div class="notes-label">Notes</div>
                <p>{{ detailsDialog.lead.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-modal-delete" @click="deleteLead(detailsDialog.lead)">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2.25 4.5H3.75H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5H14.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Delete Lead
          </button>
          <button class="btn-modal-cancel" @click="closeDetailsModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Snackbar -->
    <div v-if="snackbar.show" class="snackbar">
      <span>{{ snackbar.msg }}</span>
      <button @click="snackbar.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { auth, db } from '../firebase'
import {
  addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp,
  setDoc, updateDoc, where, increment, deleteDoc
} from 'firebase/firestore'

export default {
  name: 'StaffLeads',
  data() {
    return {
      currentUid: null,
      loading: false,
      viewMode: 'mine', // 'all' or 'mine'

      stats: {
        points: 0,
        todayPoints: 0,
        streakDays: 0,
        lastActionDate: null
      },
      unsubStats: null,

      // Badges are computed from real points (see earnedBadges), not stored/static
      badgeTiers: [
        { threshold: 100, label: 'Rookie' },
        { threshold: 500, label: 'Closer' },
        { threshold: 1500, label: 'Rainmaker' },
        { threshold: 3000, label: 'Elite' }
      ],

      leadForm: {
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        address: { street: '', city: '', state: '', zip: '', country: 'USA' },
        notes: ''
      },

      stages: ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'],
      openStages: ['new', 'contacted', 'qualified', 'proposal'],

      leads: [],
      stageFilter: '',
      search: '',
      unsubLeads: null,

      currentPage: 1,
      itemsPerPage: 10,

      logDialog: {
        open: false,
        type: 'call',
        lead: null,
        note: '',
        outcome: 'neutral'
      },
      nextAction: { type: '', dueAt: '', note: '' },

      detailsDialog: {
        open: false,
        lead: null
      },

      snackbar: { show: false, msg: '' }
    }
  },
  computed: {
    earnedBadges() {
      return this.badgeTiers
        .filter(tier => this.stats.points >= tier.threshold)
        .map(tier => tier.label)
    },
    displayTodayPoints() {
      const todayKey = new Date().toDateString()
      return this.stats.lastActionDate === todayKey ? this.stats.todayPoints : 0
    },
    displayStreakDays() {
      const now = new Date()
      const todayKey = now.toDateString()
      const yesterdayKey = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()
      const lastActionDate = this.stats.lastActionDate
      if (lastActionDate !== todayKey && lastActionDate !== yesterdayKey) return 0
      return this.stats.streakDays
    },
    nextBadgeTier() {
      return this.badgeTiers.find(tier => this.stats.points < tier.threshold) || null
    },
    progressWidth() {
      if (!this.nextBadgeTier) return 100
      const prevThreshold = [...this.badgeTiers]
        .reverse()
        .find(tier => tier.threshold <= this.stats.points)?.threshold || 0
      const span = this.nextBadgeTier.threshold - prevThreshold
      const progressed = this.stats.points - prevThreshold
      return Math.min(100, Math.round((progressed / span) * 100))
    },
    badgeRemaining() {
      if (!this.nextBadgeTier) return 0
      return this.nextBadgeTier.threshold - this.stats.points
    },
    filteredLeads() {
      const term = (this.search || '').trim().toLowerCase()
      return this.leads
        .filter(l => {
          if (this.viewMode === 'mine' && l.ownerUid !== this.currentUid) return false
          if (!this.stageFilter) {
            return this.openStages.includes(l.status)
          }
          return l.status === this.stageFilter
        })
        .filter(l => {
          if (!term) return true
          return [l.companyName, l.contactName, l.email, l.phone]
            .some(x => (x || '').toLowerCase().includes(term))
        })
    },
    totalPages() {
      return Math.ceil(this.filteredLeads.length / this.itemsPerPage)
    },
    paginatedLeads() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredLeads.slice(start, end)
    }
  },
  watch: {
    stageFilter() {
      this.currentPage = 1
      this.subscribeLeads()
    },
    viewMode() {
      this.currentPage = 1
    }
  },
  mounted() {
    this.currentUid = auth.currentUser?.uid || null

    if (this.currentUid) {
      const statsRef = doc(db, 'user_stats', this.currentUid)
      this.unsubStats = onSnapshot(statsRef, snap => {
        if (snap.exists()) {
          this.stats = Object.assign({
            todayPoints: 0,
            streakDays: 0,
            points: 0,
            lastActionDate: null
          }, snap.data())
        }
      })
    }

    this.subscribeLeads()
  },
  beforeDestroy() {
    if (this.unsubLeads) this.unsubLeads()
    if (this.unsubStats) this.unsubStats()
  },
  methods: {
    toast(msg) {
      this.snackbar = { show: true, msg }
      setTimeout(() => this.snackbar.show = false, 3000)
    },

    initials(name = '?') {
      return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
    },

    getAvatarColor(name) {
      const colors = [
        'linear-gradient(135deg, #e0c27a, #d4af37)',
        'linear-gradient(135deg, #5B8DBF, #3D6690)',
        'linear-gradient(135deg, #4F8F6F, #6FA989)',
        'linear-gradient(135deg, #6E5A8C, #4F3F68)'
      ]
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    formatDate(ts) {
      if (!ts) return '—'
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString()
      const d = new Date(ts)
      return d.toLocaleDateString()
    },

    formatDue(ts) {
      if (!ts) return ''
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString()
      return new Date(ts).toLocaleString()
    },

    isDueSoon(item) {
      const t = item?.nextAction?.dueAt
      if (!t) return false
      const due = t.seconds ? new Date(t.seconds * 1000) : new Date(t)
      const diff = due.getTime() - Date.now()
      return diff > 0 && diff < 24 * 60 * 60 * 1000
    },

    async awardPoints(uid, amount, reason = '') {
      const userRef = doc(db, 'user_stats', uid)
      const snap = await getDoc(userRef)
      const data = snap.exists() ? snap.data() : {}

      const now = new Date()
      const todayKey = now.toDateString()
      const yesterdayKey = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()
      const lastActionDate = data.lastActionDate || null

      let todayPoints = data.todayPoints || 0
      let streakDays = data.streakDays || 0

      if (lastActionDate !== todayKey) {
        todayPoints = 0
        streakDays = lastActionDate === yesterdayKey ? streakDays + 1 : 1
      }

      await setDoc(userRef, {
        points: increment(amount),
        todayPoints: todayPoints + amount,
        streakDays,
        lastActionDate: todayKey,
        lastActionAt: serverTimestamp()
      }, { merge: true })
    },

    async subscribeLeads() {
      if (this.unsubLeads) this.unsubLeads()
      this.loading = true

      const base = [collection(db, 'leads')]
      let qRef

      if (!this.stageFilter) {
        qRef = query(...base, where('status', 'in', this.openStages))
      } else {
        qRef = query(...base, where('status', '==', this.stageFilter))
      }

      this.unsubLeads = onSnapshot(qRef, snap => {
        const items = snap.docs.map(d => Object.assign({ id: d.id }, d.data()))
        this.leads = items.sort((a, b) =>
          (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        )
        this.loading = false
      }, err => {
        console.error('Leads query error:', err)
        this.toast('Could not load leads. Check Firestore rules/index.')
        this.loading = false
      })
    },

    async createLead() {
      if (!this.leadForm.companyName?.trim()) {
        this.toast('Club name is required')
        return
      }

      const payload = {
        ...this.leadForm,
        ownerUid: this.currentUid,
        status: 'new',
        score: 0,
        createdAt: serverTimestamp(),
        lastContactedAt: null,
        nextAction: null
      }

      await addDoc(collection(db, 'leads'), payload)
      await this.awardPoints(this.currentUid, 10, 'created_lead')
      this.toast('Lead saved! +10 points 🎉')

      this.leadForm = {
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        address: { street: '', city: '', state: '', zip: '', country: 'USA' },
        notes: ''
      }
    },

    openLog(lead, type) {
      this.logDialog = { open: true, type, lead, note: '', outcome: 'neutral' }
      this.nextAction = { type: '', dueAt: '', note: '' }
    },

    closeModal() {
      this.logDialog.open = false
    },

    async saveInteraction() {
      const { lead, type } = this.logDialog
      const leadRef = doc(db, 'leads', lead.id)
      const interactionsRef = collection(leadRef, 'interactions')

      await addDoc(interactionsRef, {
        type,
        note: this.logDialog.note || '',
        outcome: this.logDialog.outcome || 'neutral',
        at: serverTimestamp(),
        by: this.currentUid
      })

      const update = {
        lastContactedAt: serverTimestamp(),
        lastContactType: type
      }

      if (this.nextAction.type && this.nextAction.dueAt) {
        update.nextAction = {
          type: this.nextAction.type,
          dueAt: new Date(this.nextAction.dueAt),
          note: this.nextAction.note || ''
        }
      }

      await updateDoc(leadRef, update)

      const pointsByType = { call: 5, email: 3, meeting: 10 }
      await this.awardPoints(this.currentUid, pointsByType[type] || 2, `interaction_${type}`)

      this.logDialog.open = false
      this.toast(`Interaction saved! +${pointsByType[type] || 2} points`)
    },

    async moveStage(lead, toStage) {
      const refDoc = doc(db, 'leads', lead.id)
      await updateDoc(refDoc, { status: toStage })

      if (toStage === 'qualified') await this.awardPoints(this.currentUid, 8, 'qualified')
      if (toStage === 'proposal') await this.awardPoints(this.currentUid, 12, 'proposal')

      this.toast(`Moved to ${toStage}`)
    },

    async closeDeal(lead, won) {
      const refDoc = doc(db, 'leads', lead.id)
      await updateDoc(refDoc, {
        status: won ? 'won' : 'lost',
        closedAt: serverTimestamp()
      })

      if (won) await this.awardPoints(this.currentUid, 50, 'won')

      this.toast(won ? 'Deal WON! +50 points 🥳' : 'Marked as lost')
    },

    openDetails(lead) {
      this.detailsDialog = { open: true, lead }
    },

    closeDetailsModal() {
      this.detailsDialog.open = false
    },

    async deleteLead(lead) {
      if (!confirm(`Are you sure you want to delete ${lead.companyName}? This cannot be undone.`)) {
        return
      }

      try {
        await deleteDoc(doc(db, 'leads', lead.id))
        this.toast('Lead deleted')
        this.closeDetailsModal()
      } catch (e) {
        console.error('Delete error:', e)
        this.toast('Error deleting lead')
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Montserrat:wght@400;500;600;700;800&display=swap');

.leads-page {
  --bg-page: #16241d;
  --bg-alt: #1c3128;
  --bg-card: #203a2f;
  --bg-card-hover: #274637;
  --border-subtle: rgba(255, 255, 255, 0.09);
  --border-gold: rgba(212, 175, 55, 0.35);
  --text-primary: #F5F6F4;
  --text-secondary: rgba(245, 246, 244, 0.68);
  --text-muted: rgba(245, 246, 244, 0.48);
  --gold: #d4af37;
  --gold-light: #e0c27a;
  --gold-dark: #a8862c;

  min-height: 100vh;
  background: linear-gradient(160deg, #1a2e26 0%, #2d4a3e 55%, #16241d 100%);
  position: relative;
  overflow-x: hidden;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
  padding: 40px 0 80px;
}

.leads-page .score-value,
.leads-page .details-company {
  font-family: 'Playfair Display', 'Georgia', serif;
  letter-spacing: -0.01em;
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(150px);
  opacity: 0.2;
  pointer-events: none;
  z-index: 0;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.45), transparent);
  top: -200px;
  left: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(91, 141, 191, 0.28), transparent);
  bottom: -150px;
  right: -150px;
}

.leads-container {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
}

/* ==================== TOP SECTION ==================== */
.top-section {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

/* ==================== SCORE CARD ==================== */
.score-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.score-card:hover {
  transform: translateY(-4px);
  border-color: var(--border-gold);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
}

.score-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  color: var(--gold-light);
  font-size: 16px;
  font-weight: 700;
}

.score-stats {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}

.score-main {
  flex: 1;
}

.score-value {
  font-size: 44px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 4px;
}

.score-label {
  font-size: 14px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.score-today {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.score-today-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--gold-light);
}

.score-today-label {
  font-size: 12px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.progress-bar {
  height: 8px;
  background: var(--bg-alt);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dark), var(--gold-light));
  border-radius: 999px;
  transition: width 0.4s ease;
}

.progress-text {
  font-size: 12.5px;
  color: var(--text-muted);
}

.score-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.streak-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(212, 175, 55, 0.12);
  border: 1px solid var(--border-gold);
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--gold-light);
}

.badge-pill {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ==================== QUICK ADD CARD ==================== */
.add-lead-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 24px;
}

.add-lead-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  color: var(--gold-light);
  font-size: 16px;
  font-weight: 700;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-field {
  margin-bottom: 14px;
}

.form-field label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  padding: 10px 14px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 9px;
  color: var(--text-primary);
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  border-color: var(--gold);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.12);
}

.form-field input::placeholder,
.form-field textarea::placeholder {
  color: var(--text-muted);
}

.btn-save-lead {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  border: none;
  border-radius: 10px;
  color: #1a2e26;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save-lead:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(212, 175, 55, 0.3);
}

/* ==================== PIPELINE ==================== */
.pipeline-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 26px;
}

.pipeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.pipeline-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.lead-count {
  padding: 4px 12px;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--gold-light);
}

.pipeline-controls {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.view-toggle {
  display: flex;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 3px;
}

.view-btn {
  padding: 7px 14px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-btn.active {
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  color: #1a2e26;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  color: var(--text-muted);
}

.stage-select {
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 13.5px;
  text-transform: capitalize;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  color: var(--text-muted);
}

.search-box input {
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 14px;
  width: 160px;
}

/* ==================== TABLE ==================== */
.leads-table {
  width: 100%;
}

.table-header {
  display: grid;
  grid-template-columns: 2.4fr 1fr 1fr 1.2fr 1.6fr;
  gap: 12px;
  padding: 0 14px 12px;
  border-bottom: 1px solid var(--border-subtle);
}

.th {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}

.table-body {
  display: flex;
  flex-direction: column;
}

.lead-row {
  display: grid;
  grid-template-columns: 2.4fr 1fr 1fr 1.2fr 1.6fr;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.lead-row:hover {
  background: var(--bg-alt);
}

.lead-row.due-soon {
  background: rgba(212, 175, 55, 0.06);
}

.td-lead {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.lead-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: white;
}

.lead-info {
  min-width: 0;
}

.lead-company {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lead-contact {
  font-size: 12.5px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stage-badge {
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
  display: inline-block;
}

.stage-new { background: rgba(91, 141, 191, 0.15); border: 1px solid rgba(91, 141, 191, 0.35); color: #8fb4dc; }
.stage-contacted { background: rgba(212, 175, 55, 0.15); border: 1px solid rgba(212, 175, 55, 0.35); color: var(--gold-light); }
.stage-qualified { background: rgba(79, 143, 111, 0.15); border: 1px solid rgba(79, 143, 111, 0.35); color: #8fcfad; }
.stage-proposal { background: rgba(110, 90, 140, 0.2); border: 1px solid rgba(110, 90, 140, 0.4); color: #c3b3dd; }
.stage-won { background: rgba(79, 143, 111, 0.25); border: 1px solid rgba(79, 143, 111, 0.5); color: #a6e3c1; }
.stage-lost { background: rgba(200, 90, 80, 0.15); border: 1px solid rgba(200, 90, 80, 0.35); color: #eba79c; }

.contact-date {
  font-size: 13px;
  color: var(--text-secondary);
}

.next-action {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.action-type {
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
  color: var(--gold-light);
}

.action-due {
  font-size: 11.5px;
  color: var(--text-muted);
}

.btn-set-action {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-set-action:hover {
  border-color: var(--border-gold);
  color: var(--gold-light);
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-icon:hover {
  border-color: var(--border-gold);
  color: var(--gold-light);
}

.action-icon-view:hover {
  border-color: #5B8DBF;
  color: #8fb4dc;
}

.stage-dropdown {
  position: relative;
}

.stage-menu {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #1c3128;
  border: 1px solid var(--border-gold);
  border-radius: 10px;
  padding: 6px;
  min-width: 130px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  z-index: 10;
}

.stage-dropdown:hover .stage-menu {
  display: block;
}

.stage-menu-item {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  text-transform: capitalize;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
}

.stage-menu-item:hover {
  background: rgba(212, 175, 55, 0.1);
  color: var(--gold-light);
}

.btn-won,
.btn-lost {
  padding: 6px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: var(--bg-alt);
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.btn-won:hover {
  border-color: #4F8F6F;
  color: #8fcfad;
}

.btn-lost:hover {
  border-color: #c85a50;
  color: #eba79c;
}

/* ==================== PAGINATION ==================== */
.table-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-subtle);
}

.page-btn {
  padding: 8px 16px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: var(--border-gold);
  color: var(--gold-light);
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: var(--text-muted);
}

/* ==================== LOADING / EMPTY ==================== */
.table-loading,
.table-empty {
  padding: 60px 24px;
  text-align: center;
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.table-empty h3 {
  color: var(--text-primary);
  font-size: 18px;
}

.loader {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-subtle);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ==================== MODAL ==================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
}

.modal-card {
  background: var(--bg-card);
  border: 1px solid var(--border-gold);
  border-radius: 16px;
  padding: 0;
  max-width: 460px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-card-large {
  max-width: 560px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 24px;
  border-bottom: 1px solid var(--border-subtle);
}

.modal-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: capitalize;
}

.modal-close {
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
}

.modal-body {
  padding: 22px 24px;
}

.modal-divider {
  height: 1px;
  background: var(--border-subtle);
  margin: 16px 0;
}

.modal-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 12px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 24px;
  border-top: 1px solid var(--border-subtle);
}

.btn-modal-cancel {
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: 9px;
  color: var(--text-secondary);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}

.btn-modal-cancel:hover {
  border-color: var(--border-gold);
  color: var(--text-primary);
}

.btn-modal-save {
  padding: 10px 20px;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  border: none;
  border-radius: 9px;
  color: #1a2e26;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.btn-modal-delete {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid rgba(200, 90, 80, 0.4);
  border-radius: 9px;
  color: #eba79c;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  margin-right: auto;
}

.btn-modal-delete:hover {
  background: rgba(200, 90, 80, 0.1);
}

/* ==================== LEAD DETAILS ==================== */
.details-main {
  text-align: center;
}

.details-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0 auto 14px;
}

.details-company {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.details-stage {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
  display: inline-block;
  margin-bottom: 20px;
}

.details-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 14px;
}

.info-row a {
  color: var(--gold-light);
  text-decoration: none;
}

.info-row a:hover {
  text-decoration: underline;
}

.details-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px;
  background: var(--bg-alt);
  border-radius: 10px;
  margin-bottom: 16px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.meta-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}

.meta-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.details-notes {
  text-align: left;
  padding: 14px;
  background: var(--bg-alt);
  border-radius: 10px;
}

.notes-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.details-notes p {
  color: var(--text-secondary);
  font-size: 13.5px;
  line-height: 1.6;
  margin: 0;
}

/* ==================== SNACKBAR ==================== */
.snackbar {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 22px;
  background: #203a2f;
  border: 1px solid var(--border-gold);
  border-radius: 999px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.4);
  z-index: 3000;
}

.snackbar button {
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 14px;
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 960px) {
  .top-section {
    grid-template-columns: 1fr;
  }

  .table-header {
    display: none;
  }

  .lead-row {
    grid-template-columns: 1fr;
    gap: 10px;
    background: var(--bg-alt);
    margin-bottom: 10px;
    border: 1px solid var(--border-subtle);
  }
}

@media (max-width: 600px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .details-meta {
    grid-template-columns: 1fr;
  }
}
</style>